import { useState, type ReactNode } from 'react';
import { usesImperial } from '@/lib/units.js';

/**
 * Optional height, in the reader's units. Only used to turn standing offsets
 * into centimetres (or inches) — the grades are angles and do not need it.
 */
export function HeightField({
  heightCm,
  onSave,
}: {
  heightCm: number | null;
  onSave(cm: number | null): void;
}): ReactNode {
  const imperial = usesImperial();
  const [feet, setFeet] = useState(() => (heightCm ? String(Math.floor(heightCm / 30.48)) : ''));
  const [inches, setInches] = useState(() => (heightCm ? String(Math.round((heightCm / 2.54) % 12)) : ''));
  const [cm, setCm] = useState(() => (heightCm ? String(Math.round(heightCm)) : ''));

  const commit = (): void => {
    if (imperial) {
      const ft = Number(feet);
      const inch = Number(inches || 0);
      if (!feet) return onSave(null);
      if (Number.isFinite(ft) && Number.isFinite(inch)) onSave((ft * 12 + inch) * 2.54);
    } else {
      if (!cm) return onSave(null);
      const value = Number(cm);
      if (Number.isFinite(value)) onSave(value);
    }
  };

  const input = 'w-16 rounded-lg border border-line bg-white px-2 py-1.5 text-center';
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium" htmlFor="height-main">
        Your height <span className="font-normal text-muted">(optional)</span>
      </label>
      <div className="flex items-center gap-2 text-sm">
        {imperial ? (
          <>
            <input
              id="height-main"
              inputMode="numeric"
              className={input}
              value={feet}
              onChange={(e) => setFeet(e.target.value.replace(/\D/g, ''))}
              onBlur={commit}
              placeholder="5"
            />
            ft
            <input
              id="height-inches"
              inputMode="numeric"
              className={input}
              value={inches}
              onChange={(e) => setInches(e.target.value.replace(/\D/g, ''))}
              onBlur={commit}
              placeholder="9"
            />
            in
          </>
        ) : (
          <>
            <input
              id="height-main"
              inputMode="numeric"
              className={input}
              value={cm}
              onChange={(e) => setCm(e.target.value.replace(/\D/g, ''))}
              onBlur={commit}
              placeholder="170"
            />
            cm
          </>
        )}
      </div>
      <p className="text-xs text-muted">
        Shows how far your head and shoulders sit ahead of your feet in {imperial ? 'inches' : 'centimetres'}.
        The grades do not need it.
      </p>
    </div>
  );
}
